import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  CreditCard,
  Loader2,
  ShieldCheck,
  Sparkles,
  Wallet,
} from "lucide-react";

import Button from "../../components/Button/Button";
import { apiGet, apiPost } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";

import styles from "./AddCreditSection.module.scss";

const PRESETS = [1000, 5000, 10000];
const PREVIEW_DEBOUNCE_MS = 400;

const formatINR = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const formatCredits = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  const num = Number(value);
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(num);
};

const formatRelativeDays = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = Date.now() - then;
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
};

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const AddCreditSection = ({ onNotify }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [credits, setCredits] = useState(1000);
  const [creditsInput, setCreditsInput] = useState("1000");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [isPaying, setIsPaying] = useState(false);

  const previewSeqRef = useRef(0);

  const notify = useCallback(
    (type, message) => {
      if (typeof onNotify === "function") {
        onNotify({ type, message });
      }
    },
    [onNotify],
  );

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("catalog", "/billing/summary");
      if (res?.status !== "success") {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to load billing summary",
          }),
        );
      }
      setSummary(res.data);
    } catch (err) {
      notify("error", getErrorMessage(err, "Failed to load billing summary"));
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const fetchPreview = useCallback(async (qty) => {
    const seq = ++previewSeqRef.current;
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const res = await apiPost("catalog", "/billing/price-preview", {
        type: "ADDON",
        credits: qty,
      });
      if (seq !== previewSeqRef.current) return;
      if (res?.status !== "success") {
        throw new Error(
          JSON.stringify({ message: res?.message || "Preview failed" }),
        );
      }
      setPreview(res.data);
    } catch (err) {
      if (seq !== previewSeqRef.current) return;
      setPreview(null);
      setPreviewError(getErrorMessage(err, "Unable to load preview"));
    } finally {
      if (seq === previewSeqRef.current) {
        setPreviewLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!summary || summary.requiresBasePlan) return undefined;
    if (!credits || credits <= 0) {
      setPreview(null);
      setPreviewError("");
      return undefined;
    }
    const t = setTimeout(() => fetchPreview(credits), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [credits, summary, fetchPreview]);

  const handlePresetClick = (value) => {
    setCredits(value);
    setCreditsInput(String(value));
  };

  const handleCreditsChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    setCreditsInput(raw);
    const num = Number(raw);
    setCredits(Number.isFinite(num) ? num : 0);
  };

  const openRazorpay = useCallback(
    async (orderData) => {
      const isLoaded = await loadRazorpay();
      if (!isLoaded) {
        setIsPaying(false);
        notify("error", "Razorpay SDK failed to load");
        return;
      }
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: orderData.amount,
        currency: orderData.currency || "INR",
        name: user.companyName || user.name || "My Company",
        description:
          orderData.type === "BASE"
            ? "Base Plan Credits"
            : "Additional API Credits",
        order_id: orderData.orderId,
        handler: async (response) => {
          try {
            const verifyRes = await apiPost("billing", "/verify-payment", {
              orderId: orderData.orderId,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });
            if (verifyRes?.data?.status === "Success") {
              setTimeout(() => {
                window.dispatchEvent(new Event("balance-updated"));
              }, 500);
              setIsPaying(false);
              fetchSummary();
              navigate("/billing/payment-success", {
                replace: true,
                state: { payment: verifyRes.data },
              });
              return;
            }
            setIsPaying(false);
            navigate("/billing/payment-failed", {
              replace: true,
              state: {
                errorMessage:
                  verifyRes?.data?.message || "Payment verification failed",
              },
            });
          } catch (err) {
            setIsPaying(false);
            navigate("/billing/payment-failed", {
              replace: true,
              state: { errorMessage: getErrorMessage(err, "Payment failed") },
            });
          }
        },
        modal: {
          ondismiss: () => {
            setIsPaying(false);
            notify("info", "Payment cancelled");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (res) => {
        setIsPaying(false);
        notify(
          "error",
          res?.error?.description || res?.error?.reason || "Payment failed",
        );
      });
      rzp.open();
    },
    [navigate, notify, fetchSummary],
  );

  const startPurchase = useCallback(
    async ({ type, credits: qty }) => {
      if (isPaying) return;
      setIsPaying(true);
      try {
        let quoteRes;
        try {
          quoteRes = await apiPost("catalog", "/billing/quote", {
            type,
            credits: qty,
          });
        } catch (err) {
          let parsed = null;
          try {
            parsed = JSON.parse(err.message);
          } catch {
            parsed = null;
          }
          if (parsed?.code === "BASE_PLAN_REQUIRED") {
            notify(
              "error",
              parsed?.message ||
                "A base plan must be purchased before custom credits.",
            );
            await fetchSummary();
            return;
          }
          throw err;
        }

        if (quoteRes?.status !== "success") {
          throw new Error(
            JSON.stringify({ message: quoteRes?.message || "Quote failed" }),
          );
        }

        const orderRes = await apiPost("billing", "/create-credit-order", {
          type,
          credits: qty,
        });
        if (orderRes?.status !== "success" || !orderRes?.data?.orderId) {
          throw new Error(
            JSON.stringify({
              message: orderRes?.message || "Failed to create order",
            }),
          );
        }

        await openRazorpay({
          orderId: orderRes.data.orderId,
          amount: orderRes.data.amount,
          currency: orderRes.data.currency,
          type: orderRes.data.type,
        });
      } catch (err) {
        notify("error", getErrorMessage(err, "Unable to start purchase"));
      } finally {
        setIsPaying(false);
      }
    },
    [isPaying, notify, openRazorpay, fetchSummary],
  );

  const handleBuyBase = () => {
    const qty = summary?.baseQuote?.credits;
    if (!qty) return;
    startPurchase({ type: "BASE", credits: qty });
  };

  const handleBuyAddon = () => {
    if (!credits || credits <= 0) {
      notify("error", "Please enter a valid number of credits.");
      return;
    }
    startPurchase({ type: "ADDON", credits });
  };

  const activeBasePlanDays = summary?.activeBasePlan?.daysRemaining;

  const lotsTotal = useMemo(() => {
    if (!Array.isArray(summary?.lots)) return 0;
    return summary.lots.reduce(
      (sum, lot) => sum + Number(lot?.creditsRemaining ?? lot?.credits ?? 0),
      0,
    );
  }, [summary]);

  return (
    <section className={styles["add-credit__section"]}>
      <header className={styles["add-credit__header"]}>
        <h3 className={styles["add-credit__heading"]}>Add Credits</h3>
        <p className={styles["add-credit__sub"]}>
          Top up your balance with a base plan or additional credits.
        </p>
      </header>
      {loading ? (
        <div className={styles["add-credit__loading"]}>
          <Loader2 className={styles["add-credit__spin"]} size={28} />
          <span>Loading billing summary…</span>
        </div>
      ) : summary ? (
        <div className={styles["add-credit__content"]}>
          {/* Current balance strip */}
          <div className={styles["add-credit__balance-strip"]}>
            <div className={styles["add-credit__balance-icon"]}>
              <Wallet size={20} strokeWidth={2.5} />
            </div>
            <div className={styles["add-credit__balance-info"]}>
              <span className={styles["add-credit__balance-label"]}>
                Current balance
              </span>
              <span className={styles["add-credit__balance-value"]}>
                {formatCredits(summary.balance)} credits
              </span>
            </div>
            <div className={styles["add-credit__balance-meta"]}>
              <span>Rate</span>
              <strong>₹{summary.globalRate} / credit</strong>
            </div>
          </div>

          {summary.requiresBasePlan ? (
            <>
              <div className={styles["add-credit__alert"]}>
                <AlertTriangle size={18} strokeWidth={2.5} />
                <span>
                  You must purchase the monthly base plan before buying custom
                  credits.
                </span>
              </div>

              <div className={styles["add-credit__base-card"]}>
                <div className={styles["add-credit__base-card-header"]}>
                  <Sparkles size={18} strokeWidth={2.5} />
                  <div>
                    <h4>Base Plan</h4>
                    <span className={styles["add-credit__base-badge"]}>
                      Required
                    </span>
                  </div>
                </div>

                <div className={styles["add-credit__base-credits"]}>
                  <span className={styles["add-credit__base-credits-value"]}>
                    {formatCredits(summary.baseQuote?.credits)}
                  </span>
                  <span className={styles["add-credit__base-credits-label"]}>
                    credits
                  </span>
                  <span className={styles["add-credit__base-credits-rate"]}>
                    @ ₹{summary.baseQuote?.rate} / credit
                  </span>
                </div>

                <div className={styles["add-credit__breakdown"]}>
                  <div className={styles["add-credit__breakdown-row"]}>
                    <span>Subtotal</span>
                    <span>{formatINR(summary.baseQuote?.subtotal)}</span>
                  </div>
                  <div className={styles["add-credit__breakdown-row"]}>
                    <span>GST ({summary.baseQuote?.gstPercent}%)</span>
                    <span>{formatINR(summary.baseQuote?.gstAmount)}</span>
                  </div>
                  <div
                    className={`${styles["add-credit__breakdown-row"]} ${styles["add-credit__breakdown-row--total"]}`}
                  >
                    <span>Total</span>
                    <span>{formatINR(summary.baseQuote?.total)}</span>
                  </div>
                </div>

                <Button
                  text={isPaying ? "Processing…" : "Buy Base Plan"}
                  icon={CreditCard}
                  onClick={handleBuyBase}
                  disabled={isPaying}
                />
              </div>
            </>
          ) : (
            <>
              {activeBasePlanDays !== undefined &&
                activeBasePlanDays !== null && (
                  <div className={styles["add-credit__active-strip"]}>
                    <CheckCircle2 size={18} strokeWidth={2.5} />
                    <span>
                      Active Plan:{" "}
                      <strong>
                        {activeBasePlanDays} day
                        {activeBasePlanDays === 1 ? "" : "s"} remaining
                      </strong>
                    </span>
                  </div>
                )}

              <div className={styles["add-credit__picker-card"]}>
                <h4 className={styles["add-credit__section-title"]}>
                  Buy additional credits
                </h4>

                <div className={styles["add-credit__presets"]}>
                  {PRESETS.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={`${styles["add-credit__preset"]} ${
                        credits === value
                          ? styles["add-credit__preset--active"]
                          : ""
                      }`}
                      onClick={() => handlePresetClick(value)}
                    >
                      {formatCredits(value)}
                    </button>
                  ))}
                  <span className={styles["add-credit__preset-hint"]}>
                    or enter custom
                  </span>
                </div>

                <label className={styles["add-credit__field"]}>
                  <span>Credits</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={creditsInput}
                    onChange={handleCreditsChange}
                    placeholder="Enter credit count"
                  />
                </label>

                <div className={styles["add-credit__breakdown"]}>
                  <div className={styles["add-credit__breakdown-row"]}>
                    <span>Rate</span>
                    <span>₹{preview?.rate ?? summary.globalRate} / credit</span>
                  </div>
                  <div className={styles["add-credit__breakdown-row"]}>
                    <span>Subtotal</span>
                    <span>
                      {previewLoading
                        ? "Calculating…"
                        : formatINR(preview?.subtotal)}
                    </span>
                  </div>
                  <div className={styles["add-credit__breakdown-row"]}>
                    <span>
                      GST ({preview?.gstPercent ?? summary.gstPercent}%)
                    </span>
                    <span>
                      {previewLoading
                        ? "Calculating…"
                        : formatINR(preview?.gstAmount)}
                    </span>
                  </div>
                  <div
                    className={`${styles["add-credit__breakdown-row"]} ${styles["add-credit__breakdown-row--total"]}`}
                  >
                    <span>Total</span>
                    <span>
                      {previewLoading
                        ? "Calculating…"
                        : formatINR(preview?.total)}
                    </span>
                  </div>
                </div>

                {previewError && (
                  <p className={styles["add-credit__error-text"]}>
                    {previewError}
                  </p>
                )}

                <Button
                  text={isPaying ? "Processing…" : "Buy Credits"}
                  icon={CreditCard}
                  onClick={handleBuyAddon}
                  disabled={isPaying || previewLoading || !preview}
                />
              </div>
            </>
          )}

          {/* Your Credits / Lots */}
          {Array.isArray(summary.lots) && summary.lots.length > 0 && (
            <div className={styles["add-credit__lots-card"]}>
              <div className={styles["add-credit__lots-header"]}>
                <ShieldCheck size={18} strokeWidth={2.5} />
                <h4>Your Credits</h4>
              </div>
              <p className={styles["add-credit__lots-summary"]}>
                You have{" "}
                <strong>{formatCredits(lotsTotal || summary.balance)}</strong>{" "}
                credits available:
              </p>
              <ul className={styles["add-credit__lots-list"]}>
                {summary.lots.map((lot, idx) => {
                  const remaining = lot.creditsRemaining ?? lot.credits ?? 0;
                  const sourceLabel =
                    lot.type === "BASE"
                      ? "from base plan"
                      : lot.type === "ADDON"
                        ? "custom credits"
                        : (lot.source || lot.type || "credits").toString();
                  const relative = formatRelativeDays(
                    lot.purchasedAt || lot.createdAt,
                  );
                  return (
                    <li
                      key={lot.id ?? `${lot.type ?? "lot"}-${idx}`}
                      className={styles["add-credit__lots-item"]}
                    >
                      <span className={styles["add-credit__lots-bullet"]} />
                      <span className={styles["add-credit__lots-text"]}>
                        <strong>{formatCredits(remaining)}</strong> @ ₹
                        {lot.rate}{" "}
                        <em>
                          ({sourceLabel}
                          {relative ? `, ${relative}` : ""})
                        </em>
                      </span>
                      {lot.expiresAt && (
                        <span className={styles["add-credit__lots-expiry"]}>
                          <Clock size={12} strokeWidth={2.5} />
                          expires {new Date(lot.expiresAt).toLocaleDateString()}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
};

export default AddCreditSection;
